import { RefreshCw } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CommentEntityType, CommentThreadType } from "../../../api/comments";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import ButtonV2 from "../../../components/ui/button/v2";
import { CommentButton } from "../../../components/ui/comment/CommentButton";
import { InlineLoadingSpinner } from "../../../components/ui/spinners/InlineLoadingSpinner";
import { RETRIEVING_DATA_SUBTITLE } from "../../../lib/constants";
import { cn } from "../../../lib/utils";
import {
  clearCommentsState,
  selectAllComments,
  selectCommentsIsFetching,
} from "../logic/commentsSlice";
import { fetchCommentsThunk } from "../logic/commentsThunk";
import { CommentsTrail } from "./CommentsTrail";

export interface CommentMainPageProps {
  caseId: string;
  threadType: CommentThreadType;
  entityType: CommentEntityType;
  entityId: string;
  className?: string;
}

export const CommentMainPage: React.FC<CommentMainPageProps> = ({
  caseId,
  threadType,
  entityType,
  entityId,
  className,
}) => {
  const dispatch = useAppDispatch();
  const comments = useAppSelector(selectAllComments);
  const isFetching = useAppSelector(selectCommentsIsFetching);
  const [isTrailVisible, setIsTrailVisible] = useState(false);
  const [hasStartedFetch, setHasStartedFetch] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const initialFetchAttemptedRef = useRef(false);

  const hasComments = comments.length > 0;
  const showTrail = isTrailVisible || hasComments;

  const commentsKey = useMemo(
    () => `${caseId}::${threadType}`,
    [caseId, threadType],
  );

  useEffect(() => {
    setHasStartedFetch(false);
    setHasFetchedOnce(false);
    setIsTrailVisible(false);
    initialFetchAttemptedRef.current = false;
  }, [caseId, threadType]);

  useEffect(() => {
    if (!caseId) return;
    if (comments.length > 0) return;
    if (isFetching) return;
    if (initialFetchAttemptedRef.current) return;

    initialFetchAttemptedRef.current = true;
    dispatch(
      fetchCommentsThunk({
        case_id: caseId,
        thread_type: threadType,
        entity_type: entityType,
        entity_id: entityId,
      }),
    );
  }, [
    dispatch,
    commentsKey,
    caseId,
    threadType,
    entityType,
    entityId,
    isFetching,
    comments.length,
  ]);

  useEffect(() => {
    if (isFetching) setHasStartedFetch(true);
    if (hasStartedFetch && !isFetching) setHasFetchedOnce(true);
  }, [isFetching, hasStartedFetch]);

  useEffect(() => {
    return () => {
      dispatch(clearCommentsState());
    };
  }, [dispatch]);

  const onRefresh = () => {
    dispatch(
      fetchCommentsThunk({
        case_id: caseId,
        thread_type: threadType,
        entity_type: entityType,
        entity_id: entityId,
      }),
    );
  };

  const renderHeader = () => {
    return (
      <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-[#F9FAFB]">
        <span className="font-sans font-semibold text-base leading-6 text-[#0A0A0A]">
          Comments
        </span>

        {showTrail && (
          <ButtonV2
            variant="ghost"
            size="md"
            onClick={onRefresh}
            disabled={isFetching}
            aria-label="Refresh comments"
            title="Refresh"
            prefix={
              <RefreshCw
                className={`h-4 w-4 text-[#737373] ${isFetching ? "animate-spin" : ""}`}
              />
            }
            className="h-9 px-3"
            textClassName="font-sans font-medium text-sm leading-5 text-[#0A0A0A]"
          >
            Refresh
          </ButtonV2>
        )}
      </div>
    );
  };

  const renderEmptyState = () => {
    return (
      <div className="flex justify-center px-4 py-6">
        <div className="w-[400px] h-[300px] rounded-lg border-[#E5E5E5] p-6">
          <div className="flex flex-col items-center gap-2 mt-[38px]">
            <CommentButton
              disabled
              className="border-[#E5E5E5] disabled:opacity-100 disabled:cursor-default"
            />
            <div className="font-sans font-semibold text-base leading-6 text-center text-[#0A0A0A]">
              No comments yet
            </div>
            <div className="font-sans font-normal text-sm leading-5 text-center text-[#737373]">
              Comments will appear here once added
            </div>

            <ButtonV2
              variant="primary"
              size="md"
              onClick={() => setIsTrailVisible(true)}
              className="w-[126px] h-[36px] px-4 py-2 rounded-md"
              textClassName="font-sans font-medium text-sm leading-5"
            >
              Add comment
            </ButtonV2>
          </div>
        </div>
      </div>
    );
  };

  const renderTrail = () => {
    return (
      <div className="px-4 py-4 w-full">
        <div className="w-full">
          <CommentsTrail
            case_id={caseId}
            thread_type={threadType}
            entity_type={entityType}
            entity_id={entityId}
            onClose={() => setIsTrailVisible(false)}
            skipInitialFetch
            maxHeightPx={700}
          />
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (!hasFetchedOnce && hasStartedFetch && isFetching) {
      return (
        <InlineLoadingSpinner
          message="Loading comments..."
          subtitle={RETRIEVING_DATA_SUBTITLE}
        />
      );
    }

    if (!showTrail && !hasComments) return renderEmptyState();
    return renderTrail();
  };

  return (
    <div
      className={cn(
        "max-w-8xl mx-14 mt-6 border border-gray-100 shadow-overviewCard rounded-xl bg-white overflow-hidden",
        className,
      )}
    >
      {renderHeader()}
      {renderBody()}
    </div>
  );
};

export default CommentMainPage;
